import styles from './dadosOperacao.module.scss';
import { Button, Col, Modal, Row } from 'react-dsgov';
import {
  BrButton,
  BrDivider,
  BrLoading,
  BrModal,
  BrRadioGroup,
  BrSelect,
  BrTextarea,
} from '@govbr-ds/react-components';
import InfoCircleTooltip from '../../../../../../../components/infoCircleTooltip/infoCircleTooltip';
import React, { useContext, useEffect, useState } from 'react';
import { onTextAreaChange } from '../../../../../../../utils/textAreaSize';
import { useQuery } from '@tanstack/react-query';
import { IDadosOutrosRegimes } from '../../../../../../../interfaces/IDadosOutrosRegimes';
import { useBackEndRequests } from '../../../../../lib/backEndRequests';
import { DiefCfemContext } from '../../../../../../../contexts/DiefCfemContext';
import ErrorMessage from '../../../../../../../components/errorMessage/errorMessage';
import { useNavigate } from 'react-router-dom';
import { formatarNrProcesso } from '../../../../../../../utils/formatNrProcesso';
import { formatCurrency } from '../../../../../../../utils/formatCurrency';
import { formatarCNPJOrCpf } from '../../../../../../../utils/formatCNPJOrCPF';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IDadosOperacaoOutrosRegimes {
  hasProducao: boolean;
  setHasProducao: React.Dispatch<React.SetStateAction<boolean>>;
  hasFatoGerador: boolean;
  setHasFatoGerador: React.Dispatch<React.SetStateAction<boolean>>;
  estabelecimentoMinerador: string;
  setEstabelecimentoMinerador: React.Dispatch<React.SetStateAction<string>>;
  titularOuArrendatario: number;
  setTitularOuArrendatario: React.Dispatch<React.SetStateAction<number>>;
  observacoes: string;
  setObservacoes: React.Dispatch<React.SetStateAction<string>>;
}

const DadosOperacaoOutrosRegimes = ({
  hasProducao,
  setHasProducao,
  hasFatoGerador,
  setHasFatoGerador,
  estabelecimentoMinerador,
  setEstabelecimentoMinerador,
  titularOuArrendatario,
  setTitularOuArrendatario,
  observacoes,
  setObservacoes,
}: IDadosOperacaoOutrosRegimes) => {
  const [opcoesEstabelecimentoMinerador, setOpcoesEstabelecimentoMinerador] = useState([{ label: '', value: '' }]);
  const [openModal, setOpenModal] = useState(false);

  const onObservacaoChange = function (value: string) {
    onTextAreaChange(value, observacoes, setObservacoes);
  };
  const urlParams = new URLSearchParams(window.location.search);

  const navigate = useNavigate();
  const declaracaoId = urlParams.get('id') ?? '';

  const context = useContext(DiefCfemContext);
  const { getDadosOperacaoOutrosRegimesById, getEstabelecimentosMineradores } = useBackEndRequests();

  const idOperacao = context?.idOperacaoEditar ?? '';

  const {
    data: dadoOperacao,
    isLoading,
    error,
  } = useQuery<IDadosOutrosRegimes, IErrorWithSentryId>({
    queryKey: ['dadosOutrosRegimes', idOperacao],
    queryFn: () => getDadosOperacaoOutrosRegimesById(idOperacao),
    refetchOnWindowFocus: false,
    enabled: idOperacao !== '',
    staleTime: 0,
    refetchOnMount: true,
  });

  const {
    data: estabelecimentosMineradores,
    isLoading: isLoadingEstabelecimentos,
    error: errorEstabelecimentos,
    refetch: refetchEstabelecimentos,
  } = useQuery<string[], IErrorWithSentryId>({
    queryKey: ['getEstabelecimentosMineradores', declaracaoId],
    queryFn: () => getEstabelecimentosMineradores(declaracaoId),
    refetchOnWindowFocus: false,
    enabled: declaracaoId !== '',
    staleTime: 0,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (estabelecimentosMineradores) {
      const options = estabelecimentosMineradores.map((item) => ({
        label: formatarCNPJOrCpf(item),
        value: item,
      }));
      setOpcoesEstabelecimentoMinerador(options);
    }
  }, [estabelecimentosMineradores]);

  useEffect(() => {
    setEstabelecimentoMinerador(dadoOperacao?.estabelecimentoMinerador ?? '');
    setTitularOuArrendatario(dadoOperacao?.tipoVinculo ?? 0);
    setHasFatoGerador(dadoOperacao?.houveFatoGerador ?? false);
    setHasProducao(dadoOperacao?.houveProducaoBemMineral ?? false);
    setObservacoes(dadoOperacao?.observacoes ?? '');
  }, [
    dadoOperacao?.estabelecimentoMinerador,
    dadoOperacao?.observacoes,
    dadoOperacao?.houveFatoGerador,
    dadoOperacao?.houveProducaoBemMineral,
    dadoOperacao?.tipoVinculo,
    setEstabelecimentoMinerador,
    setHasFatoGerador,
    setHasProducao,
    setObservacoes,
    setTitularOuArrendatario,
  ]);

  return (
    <div className={styles.container}>
      <Modal title='Mudar fato gerador' useScrim modalOpened={openModal}>
        <Modal.Body>
          Ao trocar para opção que não houve fato gerador, o valor da operação será zerado e as bases de cálculo
          preenchidas serão apagadas. Você tem certeza que deseja continuar?
        </Modal.Body>
        <Modal.Footer justify-content='end'>
          <Button secondary small m={2} onClick={() => setOpenModal(false)}>
            Cancelar
          </Button>
          <Button
            primary
            small
            m={2}
            onClick={() => {
              setHasFatoGerador(false);
              setOpenModal(false);
            }}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
      {isLoading || !!error ? (
        <>
          {error ? (
            <BrModal isOpen height={'fit-content'}>
              <ErrorMessage
                errorMessage={error.message}
                error={error}
                onClickFunction={() => navigate(0)}
                noBorder
                imageWidth='200px'
              />
            </BrModal>
          ) : (
            <BrLoading large />
          )}
        </>
      ) : (
        <>
          <span className={styles.title}>Informe os dados do processo minerário</span>
          <div className={styles.dadosContainer}>
            <Row>
              <Col>
                <span className={styles.label}>Processo Minerário</span>
                <p className={styles.text}>{formatarNrProcesso(dadoOperacao?.numeroProcessoMinerario || '')}</p>
              </Col>
              <Col>
                <span className={styles.label}>Substância</span>
                <p className={styles.text}>{dadoOperacao?.substancia.toString()}</p>
              </Col>
              <Col>
                <span className={styles.label}>Município/UF</span>
                <p className={styles.text}>
                  {dadoOperacao?.municipio}/{dadoOperacao?.uf}
                </p>
              </Col>
            </Row>
            <Row>
              <Col>
                <span className={styles.label}>Valor da Operação (R$)</span>
                <p className={styles.text}>{formatCurrency(dadoOperacao?.valorOperacao ?? '-')}</p>
              </Col>
              <Col>
                <span className={styles.label}>Deduções</span>
                <p className={styles.text}>{formatCurrency(dadoOperacao?.deducoes ?? '-')}</p>
              </Col>
              <Col>
                <span className={styles.label}>Período de Apuração</span>
                <p className={styles.text}>{dadoOperacao?.periodoApuracao}</p>
              </Col>
            </Row>
            <div className={styles.divider}>
              <BrDivider />
            </div>
            <Row>
              <Col>
                <span className={styles.label}>Estabelecimento Minerador</span>
                {isLoadingEstabelecimentos ? (
                  <BrLoading />
                ) : (
                  <>
                    <BrSelect
                      className={styles.select}
                      value={estabelecimentoMinerador}
                      onChange={(value: string) => setEstabelecimentoMinerador(value)}
                      options={opcoesEstabelecimentoMinerador}
                      disabled={!estabelecimentosMineradores}
                      placeholder={formatarCNPJOrCpf(estabelecimentoMinerador)}
                    />
                    {errorEstabelecimentos && (
                      <div className={styles.errorContainer}>
                        <span className={styles.errorText}>Falha na consulta de estabelecimentos mineradores</span>
                        <BrButton className={styles.linkText} onClick={() => refetchEstabelecimentos()} size='small'>
                          (Refazer consulta)
                        </BrButton>
                      </div>
                    )}
                  </>
                )}
              </Col>
              <Col className={styles.col}>
                <div className={styles.labelVinculo}>
                  <span className={styles.label}>Tipo de Vínculo</span> <br />
                </div>
                <BrRadioGroup
                  name='radiogroup'
                  onChange={(e) => setTitularOuArrendatario(parseInt(e.target.value))}
                  inline
                  options={[
                    {
                      id: '1',
                      label: 'Titular',
                      value: 1,
                    },
                    {
                      id: '2',
                      label: 'Arrendatário',
                      value: 2,
                    },
                  ]}
                  value={titularOuArrendatario}
                />
              </Col>
              <Col></Col>
            </Row>
            <div className={styles.divider}>
              <BrDivider />
            </div>
            <Row className={styles.rowProducao}>
              <Col className={styles.colToolTip}>
                <InfoCircleTooltip
                  text='Caracteriza se houve ou não produção bruta e/ou beneficiada no período de apuração declarado.'
                  id='producao'
                />
                <span className={styles.label}>Houve Produção de Bem Mineral?</span>
              </Col>
              <Col className={styles.checkbox}>
                <div
                  className={`br-switch icon ${styles.switch}`}
                  onClick={() => setHasProducao(!hasProducao)}
                  onKeyDown={() => setHasProducao(!hasProducao)}
                  role='switch'
                  aria-checked={hasProducao}
                  tabIndex={0}
                >
                  <input id='switch-icon' type='checkbox' name='switch-icon' checked={hasProducao} role='switch' />
                  <label className={styles.labelSwitch}>{hasProducao ? 'Sim' : 'Não'}</label>
                </div>
              </Col>
            </Row>
            <Row className={styles.rowProducao}>
              <Col className={styles.colToolTip}>
                <InfoCircleTooltip
                  text='Caracteriza se houve ou não fato gerador da CFEM para o período de apuração declarado.'
                  id='movimentacao'
                />
                <span className={styles.label}>Houve Fato Gerador da CFEM?</span>
              </Col>
              <Col className={styles.checkbox}>
                <div
                  className={`br-switch icon ${styles.switch}`}
                  onClick={() =>
                    !!hasFatoGerador && !!dadoOperacao?.valorOperacao
                      ? setOpenModal(true)
                      : setHasFatoGerador(!hasFatoGerador)
                  }
                  onKeyDown={() =>
                    !!hasFatoGerador && !!dadoOperacao?.valorOperacao
                      ? setOpenModal(true)
                      : setHasFatoGerador(!hasFatoGerador)
                  }
                  role='switch'
                  aria-checked={hasFatoGerador}
                  tabIndex={0}
                >
                  <input id='switch-icon' type='checkbox' name='switch-icon' checked={hasFatoGerador} role='switch' />
                  <label className={styles.labelSwitch}>{hasFatoGerador ? 'Sim' : 'Não'}</label>
                </div>
              </Col>
            </Row>
            <div className={styles.divider}>
              <BrDivider />
            </div>
            <Row>
              <Col>
                <div className={styles.colToolTip}>
                  <InfoCircleTooltip
                    text='Outras observações pertinentes a quaisquer movimentações que envolvem a tríade processo minerário, substância e município indicada acima.'
                    id='observacoes'
                  />
                  <span className={styles.label}>Observações</span>
                </div>
              </Col>
              <Col>
                <div className={styles.observacoesContainer}>
                  <BrTextarea
                    className={styles.observacoes}
                    density='large'
                    value={observacoes}
                    onChange={(e) => onObservacaoChange(e.target.value)}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </>
      )}
    </div>
  );
};

export default DadosOperacaoOutrosRegimes;
